import React, { useCallback, useEffect, useRef } from "react";
import Hls from "hls.js";
import { VIDEO_BUFFER } from "../../constants/general";
import "./VideoPlayer.css";

interface HlsVideoPlayerProps {
  src: string;
  title?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  duration?: number;
  onEnded: () => void;
  onError?: (error: string) => void;
  onTimeUpdate?: (current: number, duration: number) => void;
  className?: string;
  fillMode?: "contain" | "cover";
}

const HlsVideoPlayer: React.FC<HlsVideoPlayerProps> = ({
  src,
  title = "HLS Stream",
  autoplay = true,
  muted = true,
  loop = false,
  duration = 30,
  onEnded,
  onError,
  onTimeUpdate,
  className = "hls-video-player",
  fillMode = "cover",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const completionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isCompletedRef = useRef(false);

  const completePlayback = useCallback(() => {
    if (isCompletedRef.current) {
      return;
    }

    isCompletedRef.current = true;

    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }

    onEnded();
  }, [onEnded]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) {
      onError?.("Missing HLS source URL");
      completePlayback();
      return;
    }

    isCompletedRef.current = false;
    video.muted = muted;
    video.loop = loop;
    video.playsInline = true;

    const playSafely = () => {
      if (!autoplay) {
        return;
      }

      video.play().catch((err) => {
        video.muted = true;
        video.play().catch(() => {
          onError?.(`Failed to autoplay HLS stream: ${String(err)}`);
        });
      });
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", playSafely, { once: true });
      video.load();
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hlsRef.current = hls;

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal) {
          onError?.(`HLS fatal error (${data.type}): ${data.details}`);
          completePlayback();
        }
      });

      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        playSafely();
      });
    } else {
      onError?.("HLS is not supported by this browser");
      completePlayback();
      return;
    }

    const onTimeUpdateInternal = () => {
      const durationValue =
        Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : duration;
      onTimeUpdate?.(video.currentTime, durationValue);
    };

    const onEndedInternal = () => {
      completePlayback();
    };

    const onErrorInternal = () => {
      onError?.(
        `Video error: ${video.error?.message || "Unknown HLS playback error"}`,
      );
      completePlayback();
    };

    video.addEventListener("timeupdate", onTimeUpdateInternal);
    video.addEventListener("ended", onEndedInternal);
    video.addEventListener("error", onErrorInternal);

    completionTimerRef.current = setTimeout(
      () => {
        completePlayback();
      },
      (duration + VIDEO_BUFFER) * 1000,
    );

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdateInternal);
      video.removeEventListener("ended", onEndedInternal);
      video.removeEventListener("error", onErrorInternal);

      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [
    src,
    autoplay,
    muted,
    loop,
    duration,
    completePlayback,
    onError,
    onTimeUpdate,
  ]);

  return (
    <div
      className={`${className} video-container`}
      style={{ width: "100%", height: "100%" }}
    >
      <video
        ref={videoRef}
        style={{ width: "100%", height: "100%", objectFit: fillMode }}
        playsInline
        controls={false}
        aria-label={title}
      />
    </div>
  );
};

export default React.memo(HlsVideoPlayer);
